import { flatMap, groupBy, map, mapValues, partition } from 'lodash';
import { z } from 'zod';

export type FeatureFlagEntry = {
  workspaceId: number | null;
  name: string;
};

export type FeatureFlagSet = {
  legacy: string[];
  byWorkspace: Record<number, string[]>;
};

/**
 * Non-colliding stash on Team objects after getAccountTeams: full wire entries
 * for v5, while `featureFlags` stays the legacy string[] the redux slice expects.
 */
export const FEATURE_FLAGS_WIRE_KEY = '__featureFlagsWire' as const;

export type TeamWithFeatureFlagsWire = {
  dbSchema: string;
  featureFlags?: unknown;
  [FEATURE_FLAGS_WIRE_KEY]?: FeatureFlagEntry[];
};

const featureFlagWire$ = z.union([
  z.string(),
  z.object({
    workspaceId: z.number().int().nullish(),
    name: z.string()
  })
]);

function toFeatureFlagEntries(item: unknown): FeatureFlagEntry[] {
  const parsed = featureFlagWire$.safeParse(item);
  if (!parsed.success) {
    return [];
  }
  if (typeof parsed.data === 'string') {
    return [{ workspaceId: null, name: parsed.data }];
  }
  return [
    { workspaceId: parsed.data.workspaceId ?? null, name: parsed.data.name }
  ];
}

/**
 * The API mixes shapes: a bare string is a legacy org-wide flag, an object
 * carries the workspace it belongs to. Anything else is dropped per entry — a
 * malformed workspace id must not promote a flag to the whole organization.
 */
export function parseFeatureFlagsResponse(raw: unknown): FeatureFlagEntry[] {
  const items = z.array(z.unknown()).safeParse(raw);
  return items.success ? flatMap(items.data, toFeatureFlagEntries) : [];
}

export function toLegacyFeatureFlagNames(
  entries: FeatureFlagEntry[]
): string[] {
  return map(
    entries.filter(entry => entry.workspaceId == null),
    'name'
  );
}

export function toFeatureFlagSet(entries: FeatureFlagEntry[]): FeatureFlagSet {
  const [legacyEntries, workspaceEntries] = partition(
    entries,
    entry => entry.workspaceId == null
  );

  return {
    legacy: map(legacyEntries, 'name'),
    byWorkspace: mapValues(groupBy(workspaceEntries, 'workspaceId'), grouped =>
      map(grouped, 'name')
    )
  };
}

export function featureFlagSetToWireEntries(
  set: FeatureFlagSet
): FeatureFlagEntry[] {
  return [
    ...map(set.legacy, name => ({ workspaceId: null, name })),
    ...flatMap(set.byWorkspace, (names, workspaceId) =>
      map(names, name => ({ workspaceId: Number(workspaceId), name }))
    )
  ];
}
